/**
 * SystemStatusGate — consumes public-config to enforce maintenance mode
 * and render the site-wide announcement banner.
 *
 * Wrap the app root inside <PublicConfigProvider> with <SystemStatusGate>.
 * If `system.maintenance_mode` is true, shows a full-screen maintenance page.
 * Otherwise renders children with a banner on top (if `banner.active`).
 */

import React, { useState, useEffect } from 'react';
import { usePublicConfig } from '@/contexts/PublicConfigContext';

const BANNER_LEVEL_STYLES = {
  info: { bg: 'rgba(59, 130, 246, 0.15)', border: '#3b82f6', text: '#93c5fd' },
  warning: { bg: 'rgba(245, 158, 11, 0.15)', border: '#f59e0b', text: '#fcd34d' },
  critical: { bg: 'rgba(239, 68, 68, 0.15)', border: '#ef4444', text: '#fca5a5' },
};

function MaintenancePage({ message }) {
  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem', background: '#080b12', color: '#e5e7eb',
    }}>
      <div style={{
        maxWidth: 480, width: '100%', textAlign: 'center',
        background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 16, padding: '2rem', backdropFilter: 'blur(12px)',
      }}>
        <div style={{
          width: 64, height: 64, margin: '0 auto 1.5rem', borderRadius: 16,
          background: 'rgba(220, 38, 38, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 0.5rem' }}>Maintenance in Progress</h1>
        <p style={{ fontSize: 14, color: '#94a3b8', margin: '0 0 1.5rem' }}>{message}</p>
        <p style={{ fontSize: 12, color: '#64748b' }}>Please check back shortly.</p>
      </div>
    </div>
  );
}

function Banner({ message, level = 'info', linkUrl }) {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem('graveforge-banner-dismissed') === message) setDismissed(true);
    } catch { /* noop */ }
  }, [message]);

  if (dismissed || !message) return null;

  const style = BANNER_LEVEL_STYLES[level] || BANNER_LEVEL_STYLES.info;
  const handleDismiss = () => {
    try { sessionStorage.setItem('graveforge-banner-dismissed', message); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 1000,
      background: style.bg, borderBottom: `1px solid ${style.border}`,
      padding: '0.5rem 1rem', fontSize: 13, color: style.text,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
    }}>
      <span style={{ flex: '0 1 auto' }}>
        {linkUrl ? <a href={linkUrl} target="_blank" rel="noopener" style={{ color: style.text, textDecoration: 'underline' }}>{message}</a> : message}
      </span>
      <button
        onClick={handleDismiss}
        style={{
          background: 'transparent', border: 'none', color: style.text,
          cursor: 'pointer', padding: '0.25rem', opacity: 0.7, fontSize: 18, lineHeight: 1,
        }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

export default function SystemStatusGate({ children }) {
  const { get, loading } = usePublicConfig();

  // While public-config is still loading, render children without gating
  // (env fallbacks apply, banner/maintenance off by default).
  if (loading) return children;

  const maintenanceMode = get('system.maintenance_mode', false) === true;
  const maintenanceMessage = get('system.maintenance_message', "We're performing maintenance. Back shortly.");

  if (maintenanceMode) {
    return <MaintenancePage message={maintenanceMessage} />;
  }

  const bannerActive = get('banner.active', false) === true;
  const bannerMessage = get('banner.message', '');
  const bannerLevel = get('banner.level', 'info');
  const bannerLinkUrl = get('banner.link_url', '');

  return (
    <>
      {bannerActive && bannerMessage && (
        <Banner message={bannerMessage} level={bannerLevel} linkUrl={bannerLinkUrl} />
      )}
      {children}
    </>
  );
}
